'use client';

import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@workspace/ui/lib';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function Toggle({ className, ...props }: React.ComponentProps<typeof SwitchPrimitive.Root>) {
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      className={cn(
        'peer data-[state=unchecked]:bg-grey-400 cursor-pointer dark:data-[state=unchecked]:bg-grey-400 focus-visible:border-ring focus-visible:ring-ring/50 flex h-6 w-10 shrink-0 items-center rounded-full border border-transparent transition-all outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-pink-400 dark:data-[state=checked]:bg-pink-400',
        className
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          'pointer-events-none block size-5 rounded-full bg-neutral-100 ring-0 transition-transform data-[state=checked]:translate-x-[calc(100%-4px)] data-[state=unchecked]:translate-x-[calc(0%+2px)] dark:data-[state=checked]:bg-neutral-100 dark:data-[state=unchecked]:bg-neutral-100'
        )}
      />
    </SwitchPrimitive.Root>
  );
}

function ToggleTabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn('flex min-w-0 flex-col gap-2', className)}
      {...props}
    />
  );
}

interface ToggleTabsListProps extends React.ComponentProps<typeof TabsPrimitive.List> {
  hideInactiveLabels?: boolean;
  scrollable?: boolean;
}

function ToggleTabsList({
  className,
  hideInactiveLabels,
  scrollable = true,
  ...props
}: ToggleTabsListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const listClassName = cn(
    'bg-grey-25 text-grey-700 border-grey-1100 dark:bg-grey-1000 dark:text-grey-200 dark:border-grey-600 flex h-9 items-center justify-center rounded-2xl border p-[1px]',
    scrollable ? 'w-max min-w-full' : 'w-fit',
    hideInactiveLabels ? 'md:min-w-[176px]' : 'md:min-w-[400px]',
    className
  );

  const checkFades = useCallback(() => {
    if (scrollable && scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftFade(scrollLeft > 0);
      setShowRightFade(scrollLeft + clientWidth < scrollWidth - 1);
    }
  }, [scrollable]);

  useEffect(() => {
    if (!scrollable) return;

    const node = scrollRef.current;
    if (node) {
      node.addEventListener('scroll', checkFades, { passive: true });
      checkFades();
    }
    window.addEventListener('resize', checkFades, { passive: true });

    return () => {
      if (node) node.removeEventListener('scroll', checkFades);
      window.removeEventListener('resize', checkFades);
    };
  }, [checkFades, scrollable]);

  const rightFade = useMemo(
    () =>
      scrollable &&
      showRightFade && (
        <div className="fade-in from-background dark:from-grey-1000 pointer-events-none absolute top-0 right-0 bottom-0 z-10 h-9 w-10 bg-gradient-to-l via-transparent to-transparent" />
      ),
    [scrollable, showRightFade]
  );

  const leftFade = useMemo(
    () =>
      scrollable &&
      showLeftFade && (
        <div className="fade-in from-background dark:from-grey-1000 pointer-events-none absolute top-0 bottom-0 left-0 z-10 h-9 w-10 bg-gradient-to-r via-transparent to-transparent" />
      ),
    [scrollable, showLeftFade]
  );

  if (!scrollable) {
    return <TabsPrimitive.List data-slot="tabs-list" className={listClassName} {...props} />;
  }

  return (
    <div className="relative h-9 w-full overflow-hidden">
      {leftFade}
      {rightFade}
      <div className="scrollbar-hide h-9 w-full overflow-x-auto overflow-y-hidden" ref={scrollRef}>
        <TabsPrimitive.List data-slot="tabs-list" className={listClassName} {...props} />
      </div>
    </div>
  );
}

function ToggleTabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-2xl border border-transparent px-4 py-0.5 text-base whitespace-nowrap transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-neutral-200 data-[state=active]:font-medium data-[state=active]:text-neutral-100 data-[state=active]:shadow-sm data-[state=inactive]:font-normal dark:data-[state=active]:bg-grey-700 dark:data-[state=active]:text-neutral-100 dark:data-[state=inactive]:text-grey-300 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  );
}

export { Toggle, ToggleTabs, ToggleTabsList, ToggleTabsTrigger };
